/**
 * components/ResponsiveDataTable.js — PrimeReact `<DataTable>`-ийн ДЭЛГЭЦЭЭ
 * МЭДЭРДЭГ хувилбар.
 *
 * `ResponsiveTable.js` (antd) -тэй ИЖИЛ зарчим, ИЖИЛ CSS (`rt-*`): өргөн
 * дэлгэцэнд DataTable-тэй яг адил, нарийн дэлгэцэнд (утас) КАРТАН ЖАГСААЛТ.
 *
 * ХЭРЭГЛЭЭ — нэг мөр солино:
 *     <DataTable …>            →   <ResponsiveDataTable …>
 *       <Column …/>                   <Column …/>
 *     </DataTable>                  </ResponsiveDataTable>
 * Бусад бүх prop, `<Column>` хүүхдүүд хэвээрээ дамжина.
 *
 * ⚠ ЯАГААД `<Column>`-ыг ДАХИН АШИГЛАДАГ ВЭ:
 *   Утасны жагсаалтыг гараар бичвэл багана бүрийн `body` (төлөвийн өнгө,
 *   огнооны формат, товч…) ДАВХАР бичигдэж, нэгийг засахад нөгөө нь
 *   хоцорно. Тиймээс картын утга бүрийг тухайн `<Column>`-ийн ӨӨРИЙНХ нь
 *   `body`/`field`-ээр гаргана — ганц ч мөр хуулбарлагдахгүй.
 *
 * ДЭМЖИГДЭХ DataTable prop-ууд (нарийн дэлгэцэнд):
 *   header · emptyMessage · loading
 *   rowGroupMode="subheader" + groupRowsBy + rowGroupHeaderTemplate
 *     → бүлгийн толгой мөр (product.js «Хор саармагжуулах…» г.м.)
 *
 * Column-ыг тохируулах (заавал биш): `mobile="title|badge|actions|hide"`
 * Автомат таамаг: header "№"/"Д/д" → нуух · эхний үлдсэн → гарчиг.
 */
import React from "react";
import { DataTable } from "primereact/datatable";

import { useIsNarrow } from "src/components/ResponsiveTable";
import "src/components/responsiveTable.css";

/** `<Column>` хүүхдүүдээс баганын тодорхойлолт гаргана. */
function columnsOf(children) {
  const out = [];
  React.Children.forEach(children, (ch) => {
    if (!React.isValidElement(ch)) return;
    const p = ch.props || {};
    out.push({
      header: p.header,
      field: p.field,
      body: p.body,
      mobile: p.mobile,
    });
  });
  return out;
}

/** Утга гаргах — `body` байвал түүгээр (PrimeReact-ийн options-той). */
function cellOf(col, row, index) {
  if (typeof col.body === "function") {
    return col.body(row, { rowIndex: index, field: col.field });
  }
  if (col.field == null) return undefined;
  return String(col.field)
    .split(".")
    .reduce((a, k) => (a == null ? a : a[k]), row);
}

const isEmpty = (v) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0);

/** Баганыг үүргээр нь ангилна — ResponsiveTable-тэй ижил дүрэм. */
function classify(cols) {
  const out = { title: null, actions: [], badges: [], rows: [] };
  let titleTaken = false;
  cols.forEach((c) => {
    const t = typeof c.header === "string" ? c.header.trim() : "";
    let role = c.mobile;
    if (!role) {
      if (t === "№" || t === "Д/д") role = "hide";
      else if (t === "Үйлдэл" || t === "Үйлдлүүд") role = "actions";
      else if (!titleTaken) role = "title";
    }
    if (role === "hide") return;
    if (role === "actions") out.actions.push(c);
    else if (role === "badge") out.badges.push(c);
    else if (role === "title" && !titleTaken) {
      out.title = c;
      titleTaken = true;
    } else out.rows.push(c);
  });
  return out;
}

export default function ResponsiveDataTable({
  children,
  value,
  loading,
  header,
  emptyMessage,
  dataKey = "id",
  rowGroupMode,
  groupRowsBy,
  rowGroupHeaderTemplate,
  /** Нарийн дэлгэцийн хязгаар (px) */
  narrowAt = 820,
  ...rest
}) {
  const narrow = useIsNarrow(narrowAt);

  if (!narrow) {
    return (
      <DataTable
        value={value}
        loading={loading}
        header={header}
        emptyMessage={emptyMessage}
        dataKey={dataKey}
        rowGroupMode={rowGroupMode}
        groupRowsBy={groupRowsBy}
        rowGroupHeaderTemplate={rowGroupHeaderTemplate}
        {...rest}
      >
        {children}
      </DataTable>
    );
  }

  const rows = value || [];
  const { title, actions, badges, rows: fieldCols } = classify(columnsOf(children));

  // Бүлгийн толгой — `groupRowsBy` утга өмнөх мөрөөс ӨӨРЧЛӨГДӨХӨД гаргана
  // (PrimeReact-ийн subheader-тэй ижил: дата эрэмбэлэгдсэн гэж үзнэ).
  const grouped = rowGroupMode === "subheader" && groupRowsBy;
  let prevGroup;

  return (
    <div className="rt-list">
      {header ? <div className="rt-group">{header}</div> : null}

      {loading ? (
        <div className="rt-state">
          <i className="fas fa-circle-notch fa-spin" /> Ачаалж байна…
        </div>
      ) : !rows.length ? (
        <div className="rt-state">{emptyMessage || "Мэдээлэл алга"}</div>
      ) : (
        rows.map((r, i) => {
          const key = r?.[dataKey] ?? i;
          let head = null;
          if (grouped) {
            const g = r?.[groupRowsBy];
            if (i === 0 || g !== prevGroup) {
              head = rowGroupHeaderTemplate ? rowGroupHeaderTemplate(r) : g;
            }
            prevGroup = g;
          }
          return (
            <React.Fragment key={key}>
              {head ? <div className="rt-group">{head}</div> : null}
              <div className="rt-card">
                {badges.length || actions.length ? (
                  <div className="rt-head">
                    {badges.map((c, ci) => (
                      <span key={ci}>{cellOf(c, r, i)}</span>
                    ))}
                    <span className="rt-spacer" />
                    {actions.map((c, ci) => (
                      <span key={ci} onClick={(e) => e.stopPropagation()}>
                        {cellOf(c, r, i)}
                      </span>
                    ))}
                  </div>
                ) : null}

                {title ? <div className="rt-title">{cellOf(title, r, i)}</div> : null}

                {fieldCols.map((c, ci) => {
                  const v = cellOf(c, r, i);
                  if (isEmpty(v)) return null;
                  return (
                    <div className="rt-row" key={ci}>
                      {/* Баганын толгой утсан дээр харагдахгүй тул талбарын
                          НЭРИЙГ мөр бүрд хамт бичнэ. */}
                      <span className="k">
                        {typeof c.header === "string" ? c.header : ""}
                      </span>
                      <span className="v">{v}</span>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })
      )}
    </div>
  );
}
